import { Injectable, inject } from '@angular/core';
import { SidebarComponent, SidebarService } from '@coreui/angular';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

export type SidebarType = 'team' | 'enterprise' | 'individual';

/** 與 CoreUI `--cui-breakpoint-lg` 對齊（側欄 overlay 與 desktop 分界） */
export const APP_SHELL_MOBILE_MAX_WIDTH = 991.98;

@Injectable({
  providedIn: 'root'
})
export class SidebarStateService {
  private readonly coreUiSidebar = inject(SidebarService);

  // 不同類型的側邊欄狀態
  private sidebarStates = {
    team: new BehaviorSubject<boolean>(false),
    enterprise: new BehaviorSubject<boolean>(false),
    individual: new BehaviorSubject<boolean>(false)
  };

  private readonly sidebarToggleRequest$ = new Subject<SidebarType>();

  // 提供外部訂閱的 Observable
  getSidebarState$(type: SidebarType) {
    return this.sidebarStates[type].asObservable();
  }

  // 取得目前狀態（同步）
  getSidebarState(type: SidebarType): boolean {
    return this.sidebarStates[type].value;
  }

  // 更新狀態
  setSidebarState(type: SidebarType, isOpen: boolean): void {
    this.sidebarStates[type].next(isOpen);
  }

  // 切換開關
  toggleSidebar(type: SidebarType): void {
    this.sidebarStates[type].next(!this.sidebarStates[type].value);
  }

  /** 由頂欄選單等觸發，與 layout 內 toggleSidebar() 行為一致 */
  requestSidebarToggle(type: SidebarType): void {
    this.sidebarToggleRequest$.next(type);
  }

  onSidebarToggleRequest$(type: SidebarType) {
    return this.sidebarToggleRequest$.pipe(filter((t) => t === type));
  }

  private sidebarOffsetObserver: ResizeObserver | null = null;
  private sidebarOffsetObservedId: string | null = null;

  /** 依實際側欄 DOM 寬度同步頂欄／主區 offset（修正窄↔寬時 occupy 變數不同步） */
  syncShellSidebarOffset(isMobile: boolean, sidebarId = 'sidebar1'): void {
    const root = document.documentElement;
    const el = document.getElementById(sidebarId);
    if (!el) {
      return;
    }

    if (isMobile) {
      root.style.setProperty('--app-shell-sidebar-offset', '0px');
      return;
    }

    if (el.classList.contains('hide')) {
      root.style.setProperty('--app-shell-sidebar-offset', '0px');
      return;
    }

    const width = Math.round(el.getBoundingClientRect().width);
    const occupyRaw = getComputedStyle(root)
      .getPropertyValue('--cui-sidebar-occupy-start')
      .trim();
    const occupyPx = occupyRaw.endsWith('px')
      ? Math.round(parseFloat(occupyRaw))
      : Math.round(parseFloat(occupyRaw) || 0);
    const offset = Math.max(width > 0 ? width : 0, Number.isFinite(occupyPx) ? occupyPx : 0);
    root.style.setProperty('--app-shell-sidebar-offset', `${offset}px`);
  }

  /** 側欄寬度變化時持續同步（窄欄↔寬欄、Imaginus 浮動頂欄） */
  observeShellSidebarOffset(isMobile: () => boolean, sidebarId = 'sidebar1'): void {
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const el = document.getElementById(sidebarId);
    if (!el) {
      return;
    }
    if (this.sidebarOffsetObserver && this.sidebarOffsetObservedId === sidebarId) {
      this.syncShellSidebarOffset(isMobile());
      return;
    }
    this.sidebarOffsetObserver?.disconnect();
    this.sidebarOffsetObservedId = sidebarId;
    this.sidebarOffsetObserver = new ResizeObserver(() => {
      this.syncShellSidebarOffset(isMobile());
    });
    this.sidebarOffsetObserver.observe(el);
    this.syncShellSidebarOffset(isMobile());
  }

  disconnectShellSidebarOffsetObserver(): void {
    this.sidebarOffsetObserver?.disconnect();
    this.sidebarOffsetObserver = null;
    this.sidebarOffsetObservedId = null;
  }

  queueShellSidebarOffsetSync(isMobile: boolean, sidebarId = 'sidebar1'): void {
    const run = () => this.syncShellSidebarOffset(isMobile, sidebarId);
    queueMicrotask(run);
    requestAnimationFrame(run);
    setTimeout(run, 0);
    setTimeout(run, 50);
    setTimeout(run, 120);
  }

  /**
   * CoreUI 在窄↔寬 breakpoint 切換時會把 visible 還原成 ngOnInit 初值；
   * 若先在窄版關閉側欄，初值為 false，寬版後會留下 .hide，漢堡包只切 narrow 也無法顯示。
   */
  repairDesktopSidebarShell(
    sidebar: SidebarComponent | undefined,
    options: { narrow?: boolean; visible?: boolean; sidebarId?: string } = {},
  ): void {
    const visible = options.visible ?? true;
    const narrow = options.narrow ?? false;
    const sidebarId = options.sidebarId ?? 'sidebar1';

    const apply = (): void => {
      if (sidebar) {
        sidebar.visible = visible;
        this.coreUiSidebar.toggle({
          visible,
          mobile: false,
          narrow,
          unfoldable: false,
          sidebar,
        });
      }

      const el = document.getElementById(sidebar?.id ?? sidebarId);
      if (el) {
        if (visible) {
          el.classList.remove('hide');
          el.classList.add('show');
        } else {
          el.classList.remove('show');
          el.classList.add('hide');
        }
      }
    };

    queueMicrotask(apply);
    requestAnimationFrame(apply);
    for (const ms of [0, 50, 120, 250]) {
      setTimeout(apply, ms);
    }
  }
}
